import { DownOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Checkbox, Dropdown, Input, List } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import classes from './Dropdown.module.scss';

type Props = {
  options: { value: string; label: string }[];
  icon: React.ReactNode;
  label:
    | React.ReactNode
    | ((selected: any[], config: Props) => React.ReactNode);
  haveOptionAll?: boolean;
  value?: string[];
  onChange?: (value: string[]) => void;
};

export default function FilterDropdown(props: Props) {
  const {
    options,
    icon,
    label,
    haveOptionAll,
    onChange,
    value = undefined
  } = props;
  const [keyword, setKeyword] = useState('');
  const [open, setOpen] = useState(false);
  const [currentSelected, setCurrentSelected] = useState([]);

  const transformOptions = useMemo(() => {
    if (!options || !options.length) {
      if (haveOptionAll) {
        return [{ key: 'all', label: 'All' }];
      }
      return [];
    }
    const nextOptions = options
      .map((option) => {
        return {
          key: option.value,
          label: option.label
        };
      })
      .filter((option) => {
        return keyword
          ? option.label.toLowerCase().includes(keyword.toLowerCase())
          : true;
      });
    return haveOptionAll
      ? [{ key: 'all', label: 'All' }].concat(nextOptions)
      : nextOptions;
  }, [options, haveOptionAll, keyword]);

  const handleChangeSearch = (e) => {
    setKeyword(e.target.value);
  };

  const handleSelect = (option) => (event) => {
    const isChecked = event.target.checked;
    const isAll = option.key === 'all';

    if (isAll) {
      setCurrentSelected(
        isChecked ? transformOptions.map((item) => item.key) : []
      );
      return;
    }

    if (isChecked) {
      const newSelected = currentSelected
        .filter((value) => value !== 'all')
        .concat([option.key]);

      const optionsWithoutAll = transformOptions.filter(
        ({ key }) => key !== 'all'
      );

      setCurrentSelected(() => {
        const nextSelected =
          newSelected.length === optionsWithoutAll.length
            ? ['all', ...newSelected]
            : [...newSelected];

        return nextSelected.filter((key) =>
          haveOptionAll ? true : key !== 'all'
        );
      });
    } else {
      setCurrentSelected(
        currentSelected
          .filter((key) => key !== option.key)
          .filter((key) => key !== 'all')
      );
    }
  };

  const handleApply = () => {
    onChange(currentSelected);
    setOpen(false);
  };

  const handleReset = () => {
    onChange([]);
    setOpen(false);
  };

  const handleClearState = () => {
    const optionsWithoutAll = transformOptions.filter(
      ({ key }) => key !== 'all'
    );
    if (!Array.isArray(value) || !value.length) {
      const nextOptions = transformOptions.map((item) => item.key);
      setCurrentSelected(nextOptions);
    } else {
      const nextOptions =
        value.filter((key) => key !== 'all').length === optionsWithoutAll.length
          ? ['all', ...value.filter((key) => key !== 'all')]
          : value;
      const nextSelected = nextOptions.filter((key) =>
        haveOptionAll ? true : key !== 'all'
      );
      setCurrentSelected(nextSelected);
    }
  };

  useEffect(() => {
    handleClearState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value), open]);

  return (
    <Dropdown
      onOpenChange={(visible) => {
        setOpen(visible);
      }}
      trigger={['click']}
      menu={{ items: transformOptions, selectable: true, multiple: true }}
      open={open}
      dropdownRender={() => {
        return (
          <List
            split={false}
            className={classes.list}
            header={
              <Input
                prefix={<SearchOutlined />}
                placeholder="Search"
                onChange={handleChangeSearch}
              />
            }
            footer={
              <>
                <Button type="primary" onClick={handleApply}>
                  Apply
                </Button>
                <Button onClick={handleReset}>Reset</Button>
              </>
            }
            dataSource={transformOptions}
            renderItem={(menu: any) => {
              return (
                <List.Item key={menu.key}>
                  <Checkbox
                    indeterminate={
                      menu.key === 'all' &&
                      currentSelected.length > 0 &&
                      currentSelected.length < transformOptions.length
                    }
                    checked={currentSelected.includes(menu.key)}
                    onChange={handleSelect(menu)}
                  >
                    <List.Item.Meta title={menu.label}></List.Item.Meta>
                  </Checkbox>
                </List.Item>
              );
            }}
          ></List>
        );
      }}
    >
      <Button className={classes.dropdownBtn} type="text" icon={icon}>
        {typeof label === 'function' ? label(currentSelected, props) : label}
        <DownOutlined />
        {/* {open ? <UpOutlined></UpOutlined> : <DownOutlined />} */}
      </Button>
    </Dropdown>
  );
}
